import type { AuthorizationScope, GlobalFilters } from '@aurora-pvi/contracts';
import type {
  Application,
  AuroraDataset,
  CostActual,
  Person,
  Product,
  Team,
} from '@aurora-pvi/seed';
import { groupBy, indexBy } from '@aurora-pvi/seed';
import type { SeededPersona } from './types';

/**
 * Scope narrowing and filtering.
 *
 * Two rules are enforced here rather than in each repository, because getting
 * either of them wrong in one place would be worse than not having them at all.
 *
 * 1. Span of control is applied before anything else. A department leader does
 *    not receive the enterprise numbers with a filter chip attached; they
 *    receive a different, smaller portfolio, and `isNarrowed` says so.
 *
 * 2. Roll-ups de-duplicate. Applications, cost actuals and teams reach a scope
 *    through many-to-many edges, so every collection built here is a Set keyed
 *    on the business code. An application supporting three products contributes
 *    once.
 */

export interface ScopeIndex {
  productsByCode: Map<string, Product>;
  applicationsByCode: Map<string, Application>;
  peopleByCode: Map<string, Person>;
  teamsByCode: Map<string, Team>;
  costActualsByCode: Map<string, CostActual>;
  /** Product codes beneath each hierarchy node, keyed by node business code. */
  productCodesByNode: Map<string, Set<string>>;
  /** Application codes linked to each product (approved and proposed alike). */
  applicationCodesByProduct: Map<string, Set<string>>;
  /**
   * The single scope-bearing owner of a cost actual. Residual (unallocated)
   * cost is attributed to exactly one owner so that a narrowed scope shows its
   * own gap without any parent double counting it.
   */
  costOwnerByActualCode: Map<string, string | null>;
  nodeByUuid: Map<string, { code: string; type: AuthorizationScope['scopeType']; label: string }>;
}

export function createScopeIndex(dataset: AuroraDataset): ScopeIndex {
  const productsByCode = indexBy(dataset.products, (p) => p.businessCode);
  const applicationsByCode = indexBy(dataset.applications, (a) => a.businessCode);
  const peopleByCode = indexBy(dataset.people, (p) => p.businessCode);
  const teamsByCode = indexBy(dataset.teams, (t) => t.businessCode);
  const costActualsByCode = indexBy(dataset.costActuals, (c) => c.businessCode);

  const productCodesByNode = new Map<string, Set<string>>();
  const add = (node: string, code: string): void => {
    const bucket = productCodesByNode.get(node);
    if (bucket) bucket.add(code);
    else productCodesByNode.set(node, new Set([code]));
  };

  for (const product of dataset.products) {
    add(dataset.enterprise.businessCode, product.businessCode);
    add(product.departmentCode, product.businessCode);
    add(product.valueStreamCode, product.businessCode);
    if (product.parentKind === 'ProductGroup') add(product.parentCode, product.businessCode);
    add(product.businessCode, product.businessCode);
  }
  // Nodes with no products still have to resolve, or an empty department would
  // silently fall back to the enterprise view.
  for (const group of dataset.productGroups) {
    if (!productCodesByNode.has(group.businessCode)) {
      productCodesByNode.set(group.businessCode, new Set());
    }
  }
  for (const stream of dataset.valueStreams) {
    if (!productCodesByNode.has(stream.businessCode)) {
      productCodesByNode.set(stream.businessCode, new Set());
    }
  }

  const applicationCodesByProduct = new Map<string, Set<string>>();
  for (const link of dataset.productApplicationLinks) {
    const bucket = applicationCodesByProduct.get(link.productCode);
    if (bucket) bucket.add(link.applicationCode);
    else applicationCodesByProduct.set(link.productCode, new Set([link.applicationCode]));
  }

  // Cost ownership: the primary product of an application, the first product of
  // a team, and the product named on a contract's statement of work.
  const primaryProductByApp = new Map<string, string>();
  for (const link of dataset.productApplicationLinks) {
    if (link.role === 'Primary' && !primaryProductByApp.has(link.applicationCode)) {
      primaryProductByApp.set(link.applicationCode, link.productCode);
    }
  }
  const sowProductByContract = new Map<string, string>();
  for (const sow of dataset.statementsOfWork) {
    if (sow.productCode && !sowProductByContract.has(sow.contractCode)) {
      sowProductByContract.set(sow.contractCode, sow.productCode);
    }
  }
  const costOwnerByActualCode = new Map<string, string | null>();
  for (const actual of dataset.costActuals) {
    if (actual.costObjectKind === 'Application') {
      costOwnerByActualCode.set(
        actual.businessCode,
        primaryProductByApp.get(actual.costObjectCode) ?? null,
      );
    } else if (actual.costObjectKind === 'Team') {
      const team = teamsByCode.get(actual.costObjectCode);
      costOwnerByActualCode.set(actual.businessCode, team?.productCodes[0] ?? null);
    } else {
      costOwnerByActualCode.set(
        actual.businessCode,
        sowProductByContract.get(actual.costObjectCode) ?? null,
      );
    }
  }

  const nodeByUuid = new Map<
    string,
    { code: string; type: AuthorizationScope['scopeType']; label: string }
  >();
  nodeByUuid.set(dataset.enterprise.id, {
    code: dataset.enterprise.businessCode,
    type: 'Enterprise',
    label: dataset.enterprise.name,
  });
  for (const department of dataset.departments) {
    nodeByUuid.set(department.id, {
      code: department.businessCode,
      type: 'Department',
      label: department.name,
    });
  }
  for (const stream of dataset.valueStreams) {
    nodeByUuid.set(stream.id, {
      code: stream.businessCode,
      type: 'ValueStream',
      label: stream.name,
    });
  }
  for (const group of dataset.productGroups) {
    nodeByUuid.set(group.id, {
      code: group.businessCode,
      type: 'ProductGroup',
      label: group.name,
    });
  }
  for (const product of dataset.products) {
    nodeByUuid.set(product.id, {
      code: product.businessCode,
      type: 'Product',
      label: product.name,
    });
  }

  return {
    productsByCode,
    applicationsByCode,
    peopleByCode,
    teamsByCode,
    costActualsByCode,
    productCodesByNode,
    applicationCodesByProduct,
    costOwnerByActualCode,
    nodeByUuid,
  };
}

export interface ScopedData {
  scopeCode: string;
  scopeId: string;
  scopeType: AuthorizationScope['scopeType'];
  scopeLabel: string;
  isNarrowed: boolean;
  /** Products within the caller's span, before the filter chips are applied. */
  scopeProductCodes: Set<string>;
  /** Products after scope and filters. This is what every page counts. */
  products: Product[];
  productCodes: Set<string>;
  /** De-duplicated applications supporting the filtered products. */
  applications: Application[];
  applicationCodes: Set<string>;
  teams: Team[];
  /** True when the caller can see the whole enterprise. */
  isEnterpriseScope: boolean;
}

function resolveScopeNode(
  dataset: AuroraDataset,
  index: ScopeIndex,
  user: SeededPersona,
  filters: GlobalFilters,
): {
  code: string;
  id: string;
  type: AuthorizationScope['scopeType'];
  label: string;
  isNarrowed: boolean;
} {
  const userProducts = index.productCodesByNode.get(user.scopeCode) ?? new Set<string>();
  const requestedNode = filters.scopeId ? index.nodeByUuid.get(filters.scopeId) : undefined;

  if (!requestedNode) {
    return {
      code: user.scopeCode,
      id: user.scopeId,
      type: user.scopeType,
      label: user.scopeLabel,
      // A caller who is not enterprise-wide is always seeing a narrowed view of
      // the portfolio, whether or not they asked for a narrower scope.
      isNarrowed: user.scopeType !== 'Enterprise',
    };
  }

  const requestedProducts = index.productCodesByNode.get(requestedNode.code) ?? new Set<string>();
  const withinSpan =
    user.scopeType === 'Enterprise' ||
    Array.from(requestedProducts).every((code) => userProducts.has(code));

  if (!withinSpan) {
    // The request reaches outside the caller's span. Rather than erroring, the
    // response is clamped back to what they may see and flagged as narrowed.
    return {
      code: user.scopeCode,
      id: user.scopeId,
      type: user.scopeType,
      label: user.scopeLabel,
      isNarrowed: true,
    };
  }

  const nodeId =
    Array.from(index.nodeByUuid.entries()).find(
      ([, node]) => node.code === requestedNode.code,
    )?.[0] ?? dataset.enterprise.id;

  return {
    code: requestedNode.code,
    id: nodeId,
    type: requestedNode.type,
    label: requestedNode.label,
    isNarrowed: user.scopeType !== 'Enterprise' || requestedNode.type !== 'Enterprise',
  };
}

export function applyFilters(
  dataset: AuroraDataset,
  index: ScopeIndex,
  user: SeededPersona,
  filters: GlobalFilters,
): ScopedData {
  const node = resolveScopeNode(dataset, index, user, filters);
  const scopeProductCodes = index.productCodesByNode.get(node.code) ?? new Set<string>();

  const departmentCodes = toCodeSet(filters.departmentIds, index);
  const valueStreamCodes = toCodeSet(filters.valueStreamIds, index);
  const productGroupCodes = toCodeSet(filters.productGroupIds, index);
  const productIdCodes = toCodeSet(filters.productIds, index);
  const ownerCodes = new Set(
    (filters.ownerIds ?? [])
      .map((id) => dataset.people.find((p) => p.id === id)?.businessCode)
      .filter((code): code is string => code !== undefined),
  );
  const healthSet = new Set(filters.health ?? []);
  const lifecycleSet = new Set(filters.lifecycle ?? []);
  const classificationSet = new Set(filters.classification ?? []);
  const search = (filters.search ?? '').trim().toLowerCase();

  const products = dataset.products.filter((product) => {
    if (!scopeProductCodes.has(product.businessCode)) return false;
    if (departmentCodes.size > 0 && !departmentCodes.has(product.departmentCode)) return false;
    if (valueStreamCodes.size > 0 && !valueStreamCodes.has(product.valueStreamCode)) return false;
    if (productGroupCodes.size > 0 && !productGroupCodes.has(product.parentCode)) return false;
    if (productIdCodes.size > 0 && !productIdCodes.has(product.businessCode)) return false;
    if (ownerCodes.size > 0 && !ownerCodes.has(product.ownerPersonCode)) return false;
    if (healthSet.size > 0 && !healthSet.has(product.health)) return false;
    if (lifecycleSet.size > 0 && !lifecycleSet.has(product.lifecycle)) return false;
    if (classificationSet.size > 0 && !classificationSet.has(product.classification)) return false;
    if (search && !`${product.name} ${product.businessCode}`.toLowerCase().includes(search)) {
      return false;
    }
    return true;
  });

  const productCodes = new Set(products.map((p) => p.businessCode));

  // De-duplication happens here, once, for every page that counts applications.
  const applicationCodes = new Set<string>();
  for (const code of productCodes) {
    for (const applicationCode of index.applicationCodesByProduct.get(code) ?? []) {
      applicationCodes.add(applicationCode);
    }
  }
  const applications = dataset.applications.filter((a) => applicationCodes.has(a.businessCode));

  const teams = dataset.teams.filter((team) =>
    team.productCodes.some((code) => productCodes.has(code)),
  );

  return {
    scopeCode: node.code,
    scopeId: node.id,
    scopeType: node.type,
    scopeLabel: node.label,
    isNarrowed: node.isNarrowed,
    scopeProductCodes,
    products,
    productCodes,
    applications,
    applicationCodes,
    teams,
    isEnterpriseScope: node.type === 'Enterprise',
  };
}

function toCodeSet(ids: string[] | undefined, index: ScopeIndex): Set<string> {
  if (!ids || ids.length === 0) return new Set();
  const out = new Set<string>();
  for (const id of ids) {
    const node = index.nodeByUuid.get(id);
    if (node) out.add(node.code);
  }
  return out;
}

// ── Money roll-ups ───────────────────────────────────────────────────────────

export interface CostRollup {
  allocatedCents: number;
  unallocatedCents: number;
  totalCents: number;
  coverage: number;
  byCategory: Record<'Run' | 'Enhance' | 'Transform' | 'Other', number>;
}

/**
 * Sum cost for a set of products.
 *
 * Allocated cost comes from allocation rows, each of which belongs to exactly
 * one product, so no amount is counted twice however many products share the
 * underlying application. Unallocated cost is the residual on cost actuals
 * owned by the scope; it is reported, never spread.
 */
export function rollUpCost(
  dataset: AuroraDataset,
  index: ScopeIndex,
  productCodes: ReadonlySet<string>,
  options: { includeOwnerlessResidual: boolean },
): CostRollup {
  const byCategory: CostRollup['byCategory'] = { Run: 0, Enhance: 0, Transform: 0, Other: 0 };
  let allocated = 0;

  const allocatedShareByActual = new Map<string, number>();
  for (const allocation of dataset.costAllocations) {
    allocatedShareByActual.set(
      allocation.costActualCode,
      (allocatedShareByActual.get(allocation.costActualCode) ?? 0) + allocation.share,
    );
    if (!productCodes.has(allocation.productCode)) continue;
    const actual = index.costActualsByCode.get(allocation.costActualCode);
    if (!actual) continue;
    const amount = Math.round(actual.amountCents * allocation.share);
    allocated += amount;
    byCategory[actual.category] += amount;
  }

  let unallocated = 0;
  for (const actual of dataset.costActuals) {
    const owner = index.costOwnerByActualCode.get(actual.businessCode) ?? null;
    const inScope = owner === null ? options.includeOwnerlessResidual : productCodes.has(owner);
    if (!inScope) continue;
    const share = Math.min(1, allocatedShareByActual.get(actual.businessCode) ?? 0);
    unallocated += Math.round(actual.amountCents * (1 - share));
  }

  const total = allocated + unallocated;
  return {
    allocatedCents: allocated,
    unallocatedCents: unallocated,
    totalCents: total,
    coverage: total === 0 ? 0 : Math.round((allocated / total) * 10000) / 10000,
    byCategory,
  };
}

/** Realised benefit for a set of products. One row per product; no overlap. */
export function rollUpBenefit(
  dataset: AuroraDataset,
  productCodes: ReadonlySet<string>,
): { realizedCents: number; targetCents: number } {
  let realized = 0;
  let target = 0;
  for (const benefit of dataset.benefits) {
    if (!productCodes.has(benefit.productCode)) continue;
    realized += benefit.realizedCents;
    target += benefit.targetCents;
  }
  return { realizedCents: realized, targetCents: target };
}

export { groupBy };
